<template>
  <div class="yard-view-list-view mt-4">
    <v-card>
      <v-card-title class="pa-4">
        <yard-view-list-view-header
          :toggleable-columns="toggleableColumns"
          @update:search="search = $event"
          @update:visible-columns="setVisibleHeaders"
          @update:show-waiting-assets="showWaitingAssets = $event" />
      </v-card-title>

      <v-card-text>
        <yard-view-list-view-table
          :spots="spots"
          :search="search"
          :headers="visibleHeaders"
          :waiting-assets="waitingAssets" />
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';

import useYard from '@/modules/yard/composables/useYard';
import YardViewListViewHeader from '@/modules/yard/components/ListView/YardViewListViewHeader.vue';
import YardViewListViewTable from '@/modules/yard/components/ListView/YardViewListViewTable.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewListView',
  components: {
    YardViewListViewHeader,
    YardViewListViewTable
  },
  setup() {
    const { t } = useI18n();
    const yardStore = useYard();
    const search = ref('');
    const expanded = ref([]);
    const visibleHeaders = ref([]);
    const showWaitingAssets = ref(true);

    const toggleableColumns = Object.freeze([
      {
        title: t('yard.pages.yardView.headers.assetId'),
        key: 'assetCode',
        order: 1,
        category: 'asset',
        default: true
      },
      {
        title: t('yard.pages.yardView.headers.assetType'),
        key: 'assetType',
        order: 2,
        category: 'asset',
        default: true
      },
      {
        title: t('yard.pages.yardView.headers.carrierCompany'),
        key: 'carrierCompany',
        order: 3,
        category: 'carrier',
        default: true
      },
      {
        title: t('yard.pages.yardView.headers.carrierVisitType'),
        key: 'carrierVisitType',
        order: 4,
        category: 'carrier',
        default: true
      },
      {
        title: t('yard.pages.yardView.headers.driversPhoneNumber'),
        key: 'carrierPhone',
        order: 5,
        category: 'carrier'
      },
      { title: t('global.basicTerms.area'), key: 'areaName', order: 6, default: true },
      { title: t('global.basicTerms.spot'), key: 'spotCode', order: 7, default: true },
      {
        title: t('yard.pages.yardView.headers.carrierArrivedAt'),
        key: 'carrierArrivedAt',
        order: 8,
        category: 'carrier',
        default: true
      },
      {
        title: t('yard.pages.yardView.headers.carrierTimeInYard'),
        key: 'carrierDaysAtYard',
        order: 9,
        category: 'carrier'
      },
      {
        title: t('yard.pages.yardView.headers.assetArrivedAt'),
        key: 'assetArrivedAt',
        order: 10,
        category: 'asset'
      },
      {
        title: t('yard.pages.yardView.headers.assetTimeInYard'),
        key: 'assetDaysAtYard',
        order: 11,
        category: 'asset',
        default: true
      },
      {
        title: t('assetVisit.common.terms.plannedArrivals'),
        key: 'carrierPlanned',
        order: 12,
        category: 'carrier'
      },
      {
        title: t('global.specialtyTerms.referenceNumber'),
        key: 'refNumber',
        order: 13,
        category: 'carrier',
        default: true
      },
      {
        title: t('yard.pages.yardView.headers.appointmentIdentifier'),
        key: 'carrierAppointmentIdentifier',
        order: 14,
        category: 'carrier'
      },
      {
        title: t('yard.pages.yardView.headers.assetStatus'),
        key: 'assetContainerStatus',
        order: 15,
        category: 'asset',
        default: true
      },
      {
        title: t('yard.pages.yardView.headers.assetState'),
        key: 'assetContainerState',
        order: 16,
        category: 'asset',
        default: true
      }
    ]);

    const setVisibleHeaders = selectedHeaders => {
      visibleHeaders.value = [
        { title: '', key: 'data-table-expand', sortable: false, align: 'start' },
        ...selectedHeaders,
        { title: '', key: 'actions', sortable: false, align: 'end' }
      ];
      yardStore.persistListViewColumns(selectedHeaders.map(v => v.key));
    };

    const waitingAssets = computed(() =>
      showWaitingAssets.value ? yardStore.waitingAssetVisitsRef.value : []
    );

    return {
      setVisibleHeaders,
      showWaitingAssets,
      toggleableColumns,
      visibleHeaders,
      search,
      expanded,
      spots: yardStore.spotsRef,
      waitingAssets
    };
  }
});
</script>
